import React, { useState, useRef, useEffect } from 'react';
import styles from './TypingEffectText.module.css';

export type HorizontalAlignment = 'left' | 'center' | 'right';

export interface TypingEffectTextWrapperProps {
  text: string;
  speed?: number;
  className?: string;
  style?: React.CSSProperties;
  triggerScrollY?: number;
  reserveFullWidth?: boolean; // if true, use ghost/absolute trick, else render inline
  alwaysStart?: boolean; // if true, always start typing effect on mount
  horizontalAlign?: HorizontalAlignment;
}

interface TypingEffectTextProps {
  text: string;
  speed?: number;
  start?: boolean;
  className?: string;
  style?: React.CSSProperties;
  alwaysStart?: boolean;
}

const TypingEffectText: React.FC<TypingEffectTextProps> = ({ text, alwaysStart, speed = 50, start = alwaysStart, className = '', style }) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    if (!start) {
      setDisplayed('');
      return;
    }
    setDisplayed('');
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, start]);
  // Instead of returning just the span, we return null here, as the effect is handled in the wrapper below
  return <span className={`${styles.typingEffectText} ${className}`} style={style}>{displayed}</span>;
};

const TypingEffectTextWrapper: React.FC<TypingEffectTextWrapperProps> = ({
  text,
  speed = 50,
  className = '',
  style,
  alwaysStart,
  triggerScrollY = 250,
  reserveFullWidth = true,
  horizontalAlign = 'left',
}) => {
  const [start, setStart] = useState(alwaysStart);
  const triggeredRef = useRef(false); // ensure only once
  const textRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {

    // Listen for scroll event
    const onScroll = () => {
      if (window.scrollY > triggerScrollY) setScrolled(true);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [triggerScrollY]);

  useEffect(() => {
    if (triggeredRef.current) return;
    let observer: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && scrolled && !triggeredRef.current) {
            setStart(true);
            triggeredRef.current = true;
            observer!.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      if (textRef.current) observer.observe(textRef.current);
    } else {
      // fallback: always start
      setStart(true);
    }
    return () => observer && observer.disconnect();
  }, [scrolled]);

  const resolvedTextAlign = horizontalAlign || 'left';

  // Ghost span for width reservation
  // Absolutely positioned typing text for left-to-right effect

  if (!reserveFullWidth) {
    // Inline mode: just render the typing effect as a span, no ghost/absolute
    return (
      <span
        ref={textRef as any}
        className={className}
        style={{
          ...style,
          textAlign: resolvedTextAlign,
        }}
      >
        <TypingEffectText text={text} speed={speed} start={start} style={{ textAlign: resolvedTextAlign }} />
      </span>
    );
  }

  return (
    <div
      ref={textRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        textAlign: resolvedTextAlign,
        width: style?.width || '100%',
        maxWidth: style?.maxWidth || 700,
        minHeight: style?.fontSize ? (typeof style.fontSize === 'number' ? style.fontSize * 1.2 : undefined) : undefined,
        marginLeft: style?.marginLeft || 'auto',
        marginRight: style?.marginRight || 'auto',
        paddingLeft: isSmallScreen ? '15px' : 0,
        display: 'block',
      }}
    >
      {/* Ghost span to reserve space */}
      <span
        style={{
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'pre-line',
          fontWeight: style?.fontWeight || 600,
          fontSize: style?.fontSize,
          fontFamily: style?.fontFamily,
          letterSpacing: style?.letterSpacing,
          width: '100%',
          display: 'inline-block',
          textAlign: resolvedTextAlign,
        }}
      >
        {text}
      </span>
      {/* Absolutely positioned typing effect */}
      <span
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          transform: 'translateX(-50%)',
          whiteSpace: 'pre-line',
          width: '100%',
          textAlign: resolvedTextAlign,
          fontWeight: style?.fontWeight || 600,
          fontSize: style?.fontSize,
          fontFamily: style?.fontFamily,
          letterSpacing: style?.letterSpacing,
          color: style?.color,
          display: 'inline-block',
          pointerEvents: 'auto',
        }}
        className={styles.typingEffectText}
      >
        {/* Use TypingEffectText for the actual typing effect */}
        <TypingEffectText text={text} speed={speed} start={start} />
      </span>
    </div>
  );
};

export default React.memo(TypingEffectTextWrapper);
